// See: https://codesandbox.io/p/sandbox/dnd-kit-multi-containers-lknfe?file=%2Fsrc%2Fsortable_item.js%3A1%2C1-42%2C1

import React, { useState, useRef, useEffect } from 'react'
import { FaPlus, FaEdit, FaCheckCircle } from 'react-icons/fa'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { useSortable, arrayMove, SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Stack, Box, Badge, Heading, Text, Button, Icon, FormControl, Input, Checkbox, ConfirmButton, ControlledPopover, UserChip } from '@/components/lib'
import utils from '@/utils/utils'

// Kanban board
export default function Kanban ({ containers, items, setContainers, setItems, onAddContainer, onEditContainer, onDeleteContainer, onAddItem, onEditItem, onDeleteItem, ItemEditor }) {
  const [activeId, setActiveId] = useState()
  const [activeType, setActiveType] = useState()
  const [activeEntity, setActiveEntity] = useState()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // dnd-kit reads `tolerance` unguarded for delay constraints, so it must
        // be set alongside `delay` or the first pointer move throws.
        delay: 100,
        tolerance: 5
      }
    })
  )

  function findContainer (id) {
    if (id in items) {
      return id
    }
    return Object.keys(items).find((key) => items[key].map((item) => item._id).includes(id))
  }

  function handleDragStart (event) {
    const { active } = event
    const { id } = active
    setActiveId(id)
    setActiveType(active.data.current.type)
    if (active.data.current.type === 'item') {
      const container = findContainer(id)
      setActiveEntity(items[container].find((item) => item._id === id))
    } else if (active.data.current.type === 'container') {
      setActiveEntity(containers.find((container) => container._id === id))
    }
  }

  function handleDragOver (event) {
    const { active, over } = event
    const { id } = active
    const { id: overId } = over

    if (activeType === 'item') {
      const activeContainer = findContainer(id)
      const overContainer = findContainer(overId)

      if (
        !activeContainer ||
        !overContainer ||
        activeContainer === overContainer
      ) {
        return
      }

      setItems((prev) => {
        const activeItems = prev[activeContainer]
        const overItems = prev[overContainer]

        // Find the indexes for the items
        const activeIndex = activeItems.map((item) => item._id).indexOf(id)
        const overIndex = overItems.map((item) => item._id).indexOf(overId)

        let newIndex
        if (overId in prev) {
          // We're at the root droppable of a container
          newIndex = overItems.length + 1
        } else {
          const isBelowLastItem =
            over &&
            overIndex === overItems.length - 1

          const modifier = isBelowLastItem ? 1 : 0

          newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1
        }

        return {
          ...prev,
          [activeContainer]: [
            ...prev[activeContainer].filter((item) => item._id !== active.id)
          ],
          [overContainer]: [
            ...prev[overContainer].slice(0, newIndex),
            items[activeContainer][activeIndex],
            ...prev[overContainer].slice(newIndex, prev[overContainer].length)
          ]
        }
      })
    } else if (activeType === 'container') {
      const activeIndex = containers.findIndex((container) => container._id === id)
      const overIndex = containers.findIndex((container) => container._id === overId)

      if (activeIndex !== overIndex) {
        setContainers(arrayMove(containers, activeIndex, overIndex))
      }
    }
  }

  function handleDragEnd (event) {
    const { active, over } = event
    const { id } = active
    const { id: overId } = over

    if (activeType === 'item') {
      const activeContainer = findContainer(id)
      const overContainer = findContainer(overId)

      if (
        !activeContainer ||
        !overContainer ||
        activeContainer !== overContainer
      ) {
        return
      }

      const activeIndex = items[activeContainer].map((item) => item._id).indexOf(id)
      const overIndex = items[overContainer].map((item) => item._id).indexOf(overId)

      if (activeIndex !== overIndex) {
        setItems((items) => ({
          ...items,
          [overContainer]: arrayMove(items[overContainer], activeIndex, overIndex)
        }))
      }
      onEditItem(activeId, overContainer, { index: overIndex })
    } else if (activeType === 'container') {
      const activeIndex = containers.findIndex((container) => container._id === id)
      const overIndex = containers.findIndex((container) => container._id === overId)

      if (activeIndex !== overIndex) {
        setContainers(arrayMove(containers, activeIndex, overIndex))
      }
      onEditContainer(containers[overIndex]._id, { index: overIndex })
    }

    setActiveId(null)
    setActiveType(null)
    setActiveEntity(null)
  }

  return (
    <Stack direction='row'>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={containers.map((container) => container._id)}
          strategy={horizontalListSortingStrategy}
        >
          {containers.map((container) => (
            <SortableContainer key={container._id} container={container} items={items[container._id] || []} onEditContainer={onEditContainer} onDeleteContainer={onDeleteContainer} onAddItem={onAddItem} onEditItem={onEditItem} onDeleteItem={onDeleteItem} ItemEditor={ItemEditor} />
          ))}
          <Box p={3} rounded={3}>
            <Button variant='ghost' colorPalette='gray' size='sm' w='100%' onClick={onAddContainer}><Icon><FaPlus /></Icon> Add lane</Button>
          </Box>

          <DragOverlay>
            {activeType === 'container' && <Container isOverlay container={containers.find(c => c._id === activeId)} items={items[activeId]} />}
            {activeType === 'item' && (activeId ? <Item item={activeEntity} isOverlay /> : null)}
          </DragOverlay>
        </SortableContext>
      </DndContext>
    </Stack>
  )
}

// Kanban containers
function Container ({ isOverlay, container, items, onEditContainer, onDeleteContainer, onAddItem, onEditItem, onDeleteItem, ItemEditor }) {
  return (
    <Stack w='300px' minH='300px' p={3} rounded='lg' bg='bg.subtle' opacity={isOverlay ? 0.5 : 1}>
      <Stack direction='row' alignItems='center'>
        {container?.isCompleteLane && <Icon as={FaCheckCircle} color='fg.success' />}
        <Heading flex={1} size='md' color='fg.default' fontWeight='semibold'>{container?.name}</Heading>
        <ContainerEditor
          container={container}
          onSave={d => onEditContainer(container._id, d)}
          onDelete={() => onDeleteContainer(container._id)}
        />
      </Stack>
      {container.description && <Text fontSize='sm' color='fg.muted'>{container.description}</Text>}
      <TaskAdder onAdd={d => onAddItem(container._id, d)} />
      <Stack>
        {items?.map((item) => (
          <SortableItem key={item._id} item={item} onEditItem={onEditItem} onDeleteItem={onDeleteItem} ItemEditor={ItemEditor} />
        ))}
      </Stack>
    </Stack>
  )
}
function SortableContainer ({ container, items, onEditContainer, onDeleteContainer, onAddItem, onEditItem, onDeleteItem, ItemEditor }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: container._id, data: { type: 'container' } })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  return (
    <SortableContext
      id={container._id}
      items={items.map((item) => item._id)}
      strategy={verticalListSortingStrategy}
    >
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <Container container={container} items={items} onEditContainer={onEditContainer} onDeleteContainer={onDeleteContainer} onAddItem={onAddItem} onEditItem={onEditItem} onDeleteItem={onDeleteItem} ItemEditor={ItemEditor} />
      </div>
    </SortableContext>
  )
}

// Kanban items
function Item ({ item, isOverlay, onEditItem, onDeleteItem, ItemEditor }) {
  const [editorOpen, setEditorOpen] = useState(false)
  return (
    <Stack cursor='pointer' bg={isOverlay ? 'bg.subtle' : 'bg.surface'} _hover={{ bg: 'bg.subtle', shadow: 'lg' }} transition='0.25s' rounded='md' shadow='md' p={3} onClick={() => setEditorOpen(true)}>
      <Stack direction='row' alignItems='top'>
        {item.status === 'completed' && <Icon as={FaCheckCircle} color='fg.success' />}
        <Heading size='sm'>{item?.title}</Heading>
      </Stack>

      <Stack direction='row' alignItems='center'>
        {item?.dueDate &&
          <Badge colorPalette={new Date(item.dueDate) < new Date() ? 'red' : 'purple'}>{utils.formatDate(item.dueDate)}</Badge>}
        {item?.query && <Badge colorPalette='blue'>Query</Badge>}
        {item?.siteMonitoringIssue && <Badge colorPalette='blue'>Site monitoring issue</Badge>}
      </Stack>
      {item?.assigneeUsers?.length > 0 && (
        <Stack direction='row' justifyContent='end' gap={1}>
          {item.assigneeUsers.map((assignee) => (
            <UserChip key={assignee._id} user={assignee} onlyAvatar compact />
          ))}
        </Stack>
      )}
      {ItemEditor && <ItemEditor isOpen={editorOpen} onClose={() => setEditorOpen(false)} task={item} onSave={d => onEditItem(item._id, item.taskLane, d)} onDelete={() => onDeleteItem(item._id)} />}
    </Stack>
  )
}
function SortableItem ({ item, onEditItem, onDeleteItem, ItemEditor }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: item._id, data: { type: 'item' } })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Item item={item} onEditItem={onEditItem} onDeleteItem={onDeleteItem} ItemEditor={ItemEditor} />
    </div>
  )
}

function TaskAdder ({ trigger, onAdd }) {
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState('')
  const inputRef = useRef()

  function add () {
    onAdd({ title })
    setIsOpen(false)
    setTitle('')
  }

  return (
    <ControlledPopover
      isOpen={isOpen} onClose={() => setIsOpen(false)}
      trigger={
        <Button
          colorPalette='primary' variant='ghost' size='xs' w='100%' onClick={() => {
            setIsOpen(true)
            setTimeout(() => inputRef.current.focus(), 100)
          }}
        ><Icon as={FaPlus} /> Add a task
        </Button>
}
      header='Add a task'
      body={
        <Box>
          <Stack spacing={3}>
            <FormControl label='Title'>
              <Input
                ref={inputRef} placeholder='Task title' value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    add()
                  }
                }}
              />
            </FormControl>
            <Stack direction='row' justifyContent='end'>
              <Button size='sm' onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button colorPalette='primary' size='sm' onClick={add}>Add task</Button>
            </Stack>
          </Stack>
        </Box>
      }
    />
  )
}

function ContainerEditor ({ trigger, container, onSave, onDelete }) {
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState(container?.name || '')
  const [description, setDescription] = useState(container?.description || '')
  const [isCompleteLane, setIsCompleteLane] = useState(container.isCompleteLane ?? false)

  useEffect(() => {
    setName(container?.name || '')
    setDescription(container?.description || '')
    setIsCompleteLane(container.isCompleteLane ?? false)
  }, [container])

  function save () {
    onSave({ name, description, isCompleteLane })
    setIsOpen(false)
  }

  return (
    <ControlledPopover
      isOpen={isOpen} onClose={() => setIsOpen(false)}
      trigger={<Button onClick={() => setIsOpen(!isOpen)} variant='ghost' size='xs' colorPalette='grey'><Icon as={FaEdit} /></Button>}
      header='Edit lane'
      body={
        <Box>
          <Stack spacing={3}>
            <FormControl label='Name'>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder='Lane name' />
            </FormControl>
            <FormControl label='Description'>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder='Lane description' />
            </FormControl>
            <FormControl>
              <Checkbox isChecked={isCompleteLane} onChange={e => setIsCompleteLane(e.checked)}>Make this lane the "completed" lane</Checkbox>
            </FormControl>
            <Button colorPalette='primary' size='sm' onClick={save}>Save</Button>
            <ConfirmButton
              trigger={<Button colorPalette='grey' variant='ghost' size='sm'>Delete this lane</Button>}
              header='Really delete this lane?'
              onConfirm={onDelete}
            />
          </Stack>
        </Box>
      }
    />
  )
}
